import { addElement, empty } from 'sesajs/dom'

import { getArgs, serializeArgs } from 'src/dev/helpers/hashArgs'
import { loadBibli } from 'src/lib/core/bibli'
import { initLocalization } from 'src/lib/i18n/loader'
import { guessLocale } from 'src/lib/i18n/main'
import { showError } from 'src/lib/utils/dom/main'

import 'src/dev/index.scss'

/**
 * js exécuté par docroot/index.html : affiche la présentation puis le form
 * (qui ouvre les pages editor.html / player.html / viewer.html dans des onglets dédiés).
 *
 * Pour la compatibilité ascendante d’anciennes urls (qui utilisaient #action=edit|play|show
 * dans le hash de index.html), on redirige vers la page dédiée correspondante.
 * Ce fallback de compatibilité est à supprimer à partir de 2028.
 */

const pageByAction: Record<string, string> = {
  edit: 'editor.html',
  play: 'player.html',
  show: 'viewer.html'
}

/**
 * Charge les contenus d’après options ou l’url, si rien n’est précisé ça affiche le form pour lister les sections
 */
async function start (): Promise<void> {
  // si on a une searchString on la converti en hash, pour permettre les deux simplement
  if (window.location.search.length > 0) {
    window.location.href = `/#${window.location.search.substring(1)}`
    return
  }

  // les arguments du hash
  const args = getArgs()
  const isDebug = args.debug === '1'
  const logIfDebug = isDebug
    ? console.debug.bind(console)
    : () => undefined

  logIfDebug(`start avec ${window.location.hash || '#'}#`, args)

  // on affecte ça en global si on nous le demande
  if (args.mtgUrl) {
    window.mtgUrl = args.mtgUrl
  }

  // #wait signifie ne rien faire, en attendant qu’un window.xxx() soit appelé par ailleurs
  // (testBrowser utilise ça)
  if (args.wait) {
    logIfDebug('wait demandé, on charge lazyLoader et on attend.')
    // il faut tout virer et ne remettre qu’un #playContainer
    empty(document.body)
    addElement(document.body, 'div', { id: 'playContainer' })
    // puis charger lazyLoader pour déclarer nos fcts globales (et attendre qu’elles soient appelées)
    // (c’est pas un module, on est au courant et osef car on récupère rien)
    // @ts-ignore TS7016: Could not find a declaration file for module …
    await import('src/lazyLoader')
    return
  }

  // testBrowser charge un rid sans conversion (pour tester le chargement des graphes v1 tels quels)
  // (utilisé par testBrowser/tasks/loadAll en mode USAGE)
  if (args.testBrowser && args.rid) {
    logIfDebug(`testBrowser charge le rid ${args.rid} sans conversion`)
    empty(document.body)
    const ct = addElement(document.body, 'div')
    // @ts-ignore TS7016: Could not find a declaration file for module …
    await import('src/lazyLoader')
    const { rid } = args
    const { graphe, ressource: { langue } } = await loadBibli(rid)
    const locale = guessLocale(langue)
    if (typeof window.spPlay === 'function') window.spPlay(ct, { graphe, locale })
    else console.error(new Error('sesaparcours n’est pas correctement chargé'))
    return
  }

  // Compatibilité ascendante d’anciennes urls avec #action=edit|play|show
  // Ce fallback est à supprimer à partir de 2028.
  if (args.action != null && pageByAction[args.action] != null) {
    const page = pageByAction[args.action]
    const { action, ...rest } = args
    void action
    const restHash = serializeArgs(rest)
    logIfDebug(`redirige ${args.action} vers ${page}#${restHash}`)
    window.location.replace(`${page}#${restHash}`)
    return
  }

  // si on a ni wait ni action, on initialise la locale
  // (utile pour les anciennes urls qui pourraient tomber ici) puis on affiche le form
  await initLocalization()
  const { default: showForm } = await import('src/dev/helpers/start.form')
  await showForm()
} // start

// et on lance notre fct principale
start().catch(showError)
